import { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls =
  "w-full rounded-lg border border-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand";

export default function SharePanel() {
  const [settings, setSettings] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.origin + "/";

  const load = () =>
    api
      .get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Errore nel caricamento delle impostazioni"));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { protected: settings.protected, password: password || null });
      setPassword("");
      toast.success("Impostazioni di condivisione salvate");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore nel salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  if (!settings) return <div className="font-mono text-xs text-slate-400 uppercase tracking-widest">Caricamento…</div>;

  return (
    <div className="max-w-xl" data-testid="share-panel">
      <h2 className="font-display font-extrabold text-xl tracking-tight text-ink">Condivisione</h2>
      <p className="text-sm text-slate-500 mt-0.5 mb-6">Gestisci il link pubblico della presentazione.</p>

      <div className="bg-white border border-line rounded-xl p-5 space-y-6">
        <div>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Link di condivisione</span>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg border border-line bg-mist px-3 py-2.5 font-mono text-xs text-slate-600 truncate" data-testid="share-url-display">
              {shareUrl}
            </div>
            <button
              data-testid="share-copy-url-button"
              onClick={copy}
              className="inline-flex items-center gap-2 rounded-lg bg-ink text-white text-xs font-display font-semibold px-4 py-2.5 hover:bg-graphite transition-colors"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copiato" : "Copia"}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-line pt-5">
          <div>
            <div className="font-display font-semibold text-sm text-ink">Protezione con password</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Se attiva, chi apre il link dovrà inserire la password per vedere il deck.
            </p>
          </div>
          <button
            data-testid="share-protected-switch"
            role="switch"
            aria-checked={settings.protected}
            onClick={() => setSettings((s) => ({ ...s, protected: !s.protected }))}
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ml-4 ${settings.protected ? "bg-brand" : "bg-slate-300"}`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                settings.protected ? "translate-x-[22px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        {settings.protected && (
          <div>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              Password {settings.has_password && "(già impostata — scrivi per cambiarla)"}
            </span>
            <input
              data-testid="share-new-password-input"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={settings.has_password ? "Nuova password" : "Imposta una password"}
              className={inputCls}
            />
          </div>
        )}

        <button
          data-testid="save-settings-button"
          onClick={save}
          disabled={saving || (settings.protected && !settings.has_password && !password)}
          className="rounded-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm px-6 py-2.5 transition-colors disabled:opacity-40"
        >
          {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}
